import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';

const TABS = ['Contents', 'Logistics', 'Accounting', 'Attachments'];
const CURRENCIES = ['INR', 'USD', 'EUR', 'JPY', 'GBP'];
const LINE_TYPES = ['Item', 'Service'];
const ITEM_SERVICE_TYPES = ['Item', 'Service'];
const SUMMARY_TYPES = ['No Summary', 'Summary', 'Detailed Summary'];
const UOM_OPTIONS = ['Each', 'Kg', 'Lb', 'Box', 'Pallet', 'Meter', 'Foot', 'Liter', 'Gallon', 'Set'];
const SERIES_OPTIONS = ['Primary', 'Secondary'];
const SHIPPING_TYPES = ['', 'Air', 'Road', 'Rail', 'Sea', 'Courier'];
const PAY_TERMS = ['', 'Net 15', 'Net 30', 'Net 45', 'Net 60', 'Due on Receipt', 'COD'];

const today = () => new Date().toISOString().slice(0, 10);
const fmt4 = (v) => (Number.isNaN(+v) ? '0.0000' : Number(v).toFixed(4));
const fmt2 = (v) => (Number.isNaN(+v) ? '0.00' : Number(v).toFixed(2));
const fmtCurrency = (v) =>
  Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const inp = `w-full h-[28px] px-2 text-xs border border-sap-border rounded bg-white
             focus:outline-none focus:ring-1 focus:ring-sap-blue/40 focus:border-sap-blue`;
const sel = `${inp} appearance-none`;
const lbl = 'block text-[10px] font-medium text-sap-textLight whitespace-nowrap';
const chk = 'accent-sap-blue w-3.5 h-3.5';
const ro = `${inp} bg-gray-100 text-sap-text cursor-not-allowed`;

const blankLine = () => ({
  _key: `${Date.now()}-${Math.random()}`,
  line_type: 'Item',
  item_id: '',
  item_no: '',
  bp_catalog_no: '',
  item_desc: '',
  quantity: '1.0000',
  uom: 'Each',
  unit_price: '0.0000',
});

const initialForm = () => ({
  series: 'Primary',
  doc_number: '',
  status: 'Open',
  vendor_id: '',
  vendor_code: '',
  vendor_name: '',
  contact_person: '',
  vendor_ref_no: '',
  currency: 'EUR',
  posting_date: today(),
  due_date: today(),
  document_date: today(),
  item_service_type: 'Item',
  summary_type: 'No Summary',
  tax_pct: '0.00',
  freight_charges: '0.0000',
  discount_pct: '0.00',
  rounding: false,
  rounding_amount: '0.00',
  buyer: '',
  owner: '',
  remarks: '',
  ship_to_address: '',
  bill_to_address: '',
  shipping_type: '',
  language: 'English',
  journal_remark: '',
  payment_terms: '',
  bp_project: '',
  referenced_po: '',
});

function FieldRow({ label, children, goldenLink, goldenTitle, onGoldenLink }) {
  return (
    <div>
      <div className={`${lbl} mb-0.5 flex items-center gap-1`}>
        <span>{label}</span>
        {goldenLink && (
          <button
            type="button"
            title={goldenTitle || 'Open linked master data'}
            onClick={onGoldenLink}
            className="text-amber-600 hover:text-amber-700 text-[11px] font-bold leading-none px-0.5"
          >
            ↗
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

export default function GoodsReceiptPo() {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [lines, setLines] = useState([blankLine()]);
  const [activeTab, setActiveTab] = useState(0);

  const [vendorResults, setVendorResults] = useState([]);
  const [vendorSearch, setVendorSearch] = useState('');
  const [showVendorDD, setShowVendorDD] = useState(false);
  const [vendorListLoading, setVendorListLoading] = useState(false);

  const [itemSearchIdx, setItemSearchIdx] = useState(null);
  const [itemResults, setItemResults] = useState([]);
  const [itemSearchLoading, setItemSearchLoading] = useState(false);
  const [itemSearch, setItemSearch] = useState('');
  const [showItemDD, setShowItemDD] = useState(false);
  const [itemDDRect, setItemDDRect] = useState(null);
  const itemBlurTimerRef = useRef(null);
  const activeItemInputRef = useRef(null);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const resetAll = useCallback(() => {
    setForm(initialForm());
    setLines([blankLine()]);
    setActiveTab(0);
    setVendorSearch('');
    setShowVendorDD(false);
  }, []);

  const resetDraft = useCallback(() => {
    setForm({ ...initialForm(), status: 'Draft' });
    setLines([blankLine()]);
    setActiveTab(0);
    setVendorSearch('');
    setShowVendorDD(false);
  }, []);

  useLayoutEffect(() => {
    if (!showItemDD || itemSearchIdx === null) {
      setItemDDRect(null);
      return undefined;
    }
    const updateRect = () => {
      const el = activeItemInputRef.current;
      if (!el || !(el instanceof HTMLElement)) {
        setItemDDRect(null);
        return;
      }
      const r = el.getBoundingClientRect();
      const width = Math.min(Math.max(r.width * 5, 260), 448, window.innerWidth - 16);
      const left = Math.min(Math.max(8, r.left), window.innerWidth - width - 8);
      const spaceBelow = window.innerHeight - r.bottom - 10;
      const maxHeight = Math.min(280, Math.max(96, spaceBelow));
      setItemDDRect({ top: r.bottom + 4, left, width, maxHeight });
    };
    updateRect();
    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);
    return () => {
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
    };
  }, [showItemDD, itemSearchIdx, itemResults, itemSearchLoading, itemSearch]);

  useEffect(() => {
    if (!showVendorDD) return undefined;
    const t = setTimeout(async () => {
      setVendorListLoading(true);
      try {
        const r = await api.get(`/purchase-orders/vendors?search=${encodeURIComponent(vendorSearch.trim())}`);
        setVendorResults(Array.isArray(r) ? r : []);
      } catch {
        setVendorResults([]);
      } finally {
        setVendorListLoading(false);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [vendorSearch, showVendorDD]);

  const pickVendor = (v) => {
    setForm((f) => ({
      ...f,
      vendor_id: v.vendor_id,
      vendor_code: v.vendor_code,
      vendor_name: v.vendor_name,
      contact_person: v.contact_person || f.contact_person,
      currency: v.currency || f.currency,
    }));
    setVendorSearch('');
    setShowVendorDD(false);
  };

  useEffect(() => {
    if (!showItemDD || itemSearchIdx === null) {
      setItemResults([]);
      setItemSearchLoading(false);
      return undefined;
    }
    const q = itemSearch.trim();
    const delay = q.length < 1 ? 80 : 250;
    let cancelled = false;
    setItemSearchLoading(true);
    const tmr = setTimeout(async () => {
      try {
        const r = await api.get(`/purchase-orders/items?search=${encodeURIComponent(q)}`);
        if (!cancelled) setItemResults(Array.isArray(r) ? r : []);
      } catch {
        if (!cancelled) setItemResults([]);
      } finally {
        if (!cancelled) setItemSearchLoading(false);
      }
    }, delay);
    return () => {
      cancelled = true;
      clearTimeout(tmr);
    };
  }, [itemSearch, itemSearchIdx, showItemDD]);

  const pickItem = (item) => {
    if (itemBlurTimerRef.current) {
      clearTimeout(itemBlurTimerRef.current);
      itemBlurTimerRef.current = null;
    }
    if (itemSearchIdx === null) return;
    setLines((ls) =>
      ls.map((l, i) =>
        i === itemSearchIdx
          ? {
              ...l,
              item_id: item.item_id,
              item_no: item.item_no,
              item_desc: item.description,
              bp_catalog_no:
                item.bp_catalog_hint != null && String(item.bp_catalog_hint).trim()
                  ? String(item.bp_catalog_hint).trim()
                  : l.bp_catalog_no,
              uom: 'Each',
              unit_price: fmt4(item.unit_price || 0),
            }
          : l,
      ),
    );
    setShowItemDD(false);
    setItemSearch('');
    setItemSearchIdx(null);
  };

  const updateLine = (idx, k, v) =>
    setLines((ls) => ls.map((l, i) => (i === idx ? { ...l, [k]: v } : l)));
  const addLine = () => setLines((ls) => [...ls, blankLine()]);
  const removeLine = (idx) => setLines((ls) => ls.filter((_, i) => i !== idx));

  const {
    discountAmt,
    totalBeforeDiscount,
    taxAmt,
    freight,
    roundingVal,
    grandTotal,
  } = useMemo(() => {
    const totalBeforeDiscountCalc = lines.reduce((sum, l) => {
      const qty = parseFloat(l.quantity) || 0;
      const price = parseFloat(l.unit_price) || 0;
      return sum + qty * price;
    }, 0);
    const discPct = parseFloat(form.discount_pct) || 0;
    const discountAmtCalc = totalBeforeDiscountCalc * (discPct / 100);
    const sub = totalBeforeDiscountCalc - discountAmtCalc;
    const tax = sub * ((parseFloat(form.tax_pct) || 0) / 100);
    const fr = parseFloat(form.freight_charges) || 0;
    const roundV = form.rounding ? parseFloat(form.rounding_amount) || 0 : 0;
    return {
      totalBeforeDiscount: totalBeforeDiscountCalc,
      discountAmt: discountAmtCalc,
      subtotal: sub,
      taxAmt: tax,
      freight: fr,
      roundingVal: roundV,
      grandTotal: sub + tax + fr + roundV,
    };
  }, [lines, form.tax_pct, form.freight_charges, form.discount_pct, form.rounding, form.rounding_amount]);

  const openVendorMaster = () => {
    if (form.vendor_id) navigate(`/vendor/${form.vendor_id}`);
  };

  return (
    <div className="space-y-2 pb-8">
      <div className="text-2xs text-sap-textLight flex items-center gap-1">
        <button type="button" onClick={() => navigate('/purchasing/orders')} className="hover:text-sap-blue transition-colors">
          Purchasing
        </button>
        <span>›</span>
        <span className="text-sap-text font-medium">Goods Receipt PO</span>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="page-title">Goods Receipt PO</h1>
        <button
          type="button"
          onClick={() => navigate('/purchasing/orders')}
          className="btn-secondary text-xs py-1.5 px-3"
        >
          Back
        </button>
      </div>

      <div className="card p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
          <div className="space-y-2">
            <FieldRow label="Vendor" goldenLink={!!form.vendor_id} goldenTitle="Vendor master data" onGoldenLink={openVendorMaster}>
              <div className="relative flex items-stretch">
                {form.vendor_code ? (
                  <div className={`${inp} flex items-center gap-2 flex-1`}>
                    <span className="font-medium text-sap-blue">{form.vendor_code}</span>
                    <span className="text-sap-textLight truncate flex-1">{form.vendor_name}</span>
                    <button
                      type="button"
                      onClick={() => {
                        setField('vendor_id', '');
                        setField('vendor_code', '');
                        setField('vendor_name', '');
                      }}
                      className="text-red-400 hover:text-red-600 text-xs"
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <>
                    <input
                      value={vendorSearch}
                      placeholder="Vendor code / name…"
                      onChange={(e) => {
                        setVendorSearch(e.target.value);
                        setShowVendorDD(true);
                      }}
                      onFocus={() => setShowVendorDD(true)}
                      onBlur={() => setTimeout(() => setShowVendorDD(false), 200)}
                      className="flex-1 min-w-0 h-[28px] px-2 text-xs border border-sap-border rounded-l bg-white border-r-0 focus:outline-none focus:ring-1 focus:ring-sap-blue/40"
                      autoComplete="off"
                    />
                    <button
                      type="button"
                      title="Choose from list"
                      aria-label="Open vendor list"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setShowVendorDD(true);
                      }}
                      className="h-[28px] w-8 shrink-0 flex items-center justify-center border border-sap-border rounded-r bg-gray-50 text-sap-textLight hover:bg-sap-blue/10 hover:text-sap-blue"
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </button>
                  </>
                )}
                {showVendorDD && !form.vendor_code && (
                  <div className="absolute z-50 left-0 top-full mt-0.5 w-full bg-white border border-sap-border rounded-lg shadow-xl max-h-48 overflow-y-auto">
                    {vendorListLoading ? (
                      <div className="px-3 py-3 text-xs text-sap-textLight text-center">Loading…</div>
                    ) : vendorResults.length === 0 ? (
                      <div className="px-3 py-2 text-xs text-sap-textLight">No vendors found</div>
                    ) : (
                      vendorResults.map((v) => (
                        <button
                          key={v.vendor_id}
                          type="button"
                          onMouseDown={() => pickVendor(v)}
                          className="w-full text-left px-3 py-1.5 text-xs hover:bg-sap-blue/10 flex items-center gap-2"
                        >
                          <span className="font-semibold text-sap-blue">{v.vendor_code}</span>
                          <span className="text-sap-textLight truncate">{v.vendor_name}</span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </FieldRow>
            <FieldRow label="Name">
              <input value={form.vendor_name} readOnly className={ro} />
            </FieldRow>
            <FieldRow label="Contact Person">
              <input
                value={form.contact_person}
                onChange={(e) => setField('contact_person', e.target.value)}
                className={inp}
              />
            </FieldRow>
            <FieldRow label="Vendor Ref. No.">
              <input value={form.vendor_ref_no} onChange={(e) => setField('vendor_ref_no', e.target.value)} className={inp} />
            </FieldRow>
            <FieldRow label="BP Currency">
              <div className="flex gap-2 items-center">
                <select value={form.currency} onChange={(e) => setField('currency', e.target.value)} className={`${sel} flex-1`}>
                  {CURRENCIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
                <input readOnly value={form.currency} className={`${ro} w-16 text-center shrink-0`} title="Document currency" />
              </div>
            </FieldRow>
          </div>

          <div className="space-y-2">
            <FieldRow label="No.">
              <div className="flex gap-2">
                <select value={form.series} onChange={(e) => setField('series', e.target.value)} className={`${sel} w-[100px]`}>
                  {SERIES_OPTIONS.map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
                <input
                  value={form.doc_number}
                  onChange={(e) => setField('doc_number', e.target.value)}
                  placeholder="<New>"
                  className={`${inp} flex-1`}
                />
              </div>
            </FieldRow>
            <FieldRow label="Status">
              <input readOnly value={form.status} className={ro} />
            </FieldRow>
            <FieldRow label="Posting Date">
              <input type="date" value={form.posting_date} onChange={(e) => setField('posting_date', e.target.value)} className={inp} />
            </FieldRow>
            <FieldRow label="Due Date">
              <input type="date" value={form.due_date} onChange={(e) => setField('due_date', e.target.value)} className={inp} />
            </FieldRow>
            <FieldRow label="Document Date">
              <input
                type="date"
                value={form.document_date}
                onChange={(e) => setField('document_date', e.target.value)}
                className={inp}
              />
            </FieldRow>
          </div>
        </div>
      </div>

      <div className="flex gap-0 border-b border-sap-border/60">
        {TABS.map((t, i) => (
          <button
            key={t}
            type="button"
            onClick={() => setActiveTab(i)}
            className={`px-4 py-2 text-xs font-medium transition-colors border-b-2 -mb-px
              ${activeTab === i ? 'border-sap-blue text-sap-blue' : 'border-transparent text-sap-textLight hover:text-sap-text'}`}
          >
            {t}
          </button>
        ))}
      </div>

      {activeTab === 0 && (
        <div className="space-y-2">
          <div className="bg-white rounded-2xl shadow-sm p-3 flex flex-wrap justify-between items-end gap-4">
            <FieldRow label="Item/Service Type">
              <select
                value={form.item_service_type}
                onChange={(e) => setField('item_service_type', e.target.value)}
                className={`${sel} w-36`}
              >
                {ITEM_SERVICE_TYPES.map((t) => (
                  <option key={t}>{t}</option>
                ))}
              </select>
            </FieldRow>
            <FieldRow label="Summary Type">
              <select value={form.summary_type} onChange={(e) => setField('summary_type', e.target.value)} className={`${sel} w-44`}>
                {SUMMARY_TYPES.map((t) => (
                  <option key={t}>{t}</option>
                ))}
              </select>
            </FieldRow>
          </div>

          <div className="bg-white rounded-2xl shadow-sm overflow-x-auto">
            <table className="w-full text-xs min-w-[900px]">
              <thead>
                <tr className="bg-sap-blue/5 border-b border-sap-border/40 text-sap-textLight">
                  <th className="text-left px-2 py-2 font-semibold text-2xs uppercase w-8">#</th>
                  <th className="text-left px-2 py-2 font-semibold text-2xs uppercase w-24">Type</th>
                  <th className="text-left px-2 py-2 font-semibold text-2xs uppercase min-w-[120px]">Item No.</th>
                  <th className="text-left px-2 py-2 font-semibold text-2xs uppercase w-28">BP Catalog No.</th>
                  <th className="text-left px-2 py-2 font-semibold text-2xs uppercase min-w-[180px]">Item Description</th>
                  <th className="text-right px-2 py-2 font-semibold text-2xs uppercase w-24">Quantity</th>
                  <th className="text-left px-2 py-2 font-semibold text-2xs uppercase w-28">UoM Name</th>
                  <th className="text-right px-2 py-2 font-semibold text-2xs uppercase w-28">Unit</th>
                  <th className="w-8" />
                </tr>
              </thead>
              <tbody>
                {lines.map((l, idx) => (
                  <tr key={l._key} className="border-t border-sap-border/30 hover:bg-sap-blueLight/20">
                    <td className="px-2 py-1 text-sap-textLight">{idx + 1}</td>
                    <td className="px-1 py-1">
                      <select
                        value={l.line_type}
                        onChange={(e) => updateLine(idx, 'line_type', e.target.value)}
                        className="w-full border border-sap-border rounded px-1 py-1 text-xs bg-white"
                      >
                        {LINE_TYPES.map((t) => (
                          <option key={t}>{t}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-1 py-1">
                      <input
                        value={itemSearchIdx === idx ? itemSearch : l.item_no}
                        ref={(el) => {
                          if (itemSearchIdx === idx) activeItemInputRef.current = el;
                        }}
                        onChange={(e) => {
                          activeItemInputRef.current = e.currentTarget;
                          setItemSearch(e.target.value);
                          setItemSearchIdx(idx);
                          setShowItemDD(true);
                        }}
                        onFocus={(e) => {
                          if (itemBlurTimerRef.current) {
                            clearTimeout(itemBlurTimerRef.current);
                            itemBlurTimerRef.current = null;
                          }
                          activeItemInputRef.current = e.currentTarget;
                          setItemSearchIdx(idx);
                          setItemSearch(l.item_no || '');
                          setShowItemDD(true);
                        }}
                        onBlur={() => {
                          itemBlurTimerRef.current = setTimeout(() => {
                            setShowItemDD(false);
                            setItemSearchIdx(null);
                            setItemSearch('');
                            itemBlurTimerRef.current = null;
                          }, 200);
                        }}
                        placeholder="Search…"
                        autoComplete="off"
                        className="w-full min-w-[7rem] border border-sap-border rounded px-2 py-1 text-xs"
                      />
                    </td>
                    <td className="px-1 py-1">
                      <input
                        value={l.bp_catalog_no}
                        onChange={(e) => updateLine(idx, 'bp_catalog_no', e.target.value)}
                        className="w-full border border-sap-border rounded px-2 py-1 text-xs"
                      />
                    </td>
                    <td className="px-1 py-1">
                      <input readOnly value={l.item_desc} className={`${inp} bg-gray-50 text-sap-textLight`} />
                    </td>
                    <td className="px-1 py-1">
                      <input
                        type="number"
                        value={l.quantity}
                        onChange={(e) => updateLine(idx, 'quantity', e.target.value)}
                        className="w-full border border-sap-border rounded px-2 py-1 text-xs text-right"
                      />
                    </td>
                    <td className="px-1 py-1">
                      <select
                        value={l.uom}
                        onChange={(e) => updateLine(idx, 'uom', e.target.value)}
                        className="w-full border border-sap-border rounded px-2 py-1 text-xs bg-white"
                      >
                        {UOM_OPTIONS.map((u) => (
                          <option key={u}>{u}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-1 py-1">
                      <input
                        type="number"
                        value={l.unit_price}
                        onChange={(e) => updateLine(idx, 'unit_price', e.target.value)}
                        className="w-full border border-sap-border rounded px-2 py-1 text-xs text-right"
                      />
                    </td>
                    <td className="px-1 py-1">
                      <button
                        type="button"
                        onClick={() => removeLine(idx)}
                        disabled={lines.length === 1}
                        className="text-red-400 hover:text-red-600 disabled:opacity-30"
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button
            type="button"
            onClick={addLine}
            className="w-full py-1.5 border border-dashed border-sap-blue/40 rounded-xl text-xs text-sap-blue hover:bg-sap-blue/5 font-medium"
          >
            + Add Line
          </button>
        </div>
      )}

      {activeTab === 1 && (
        <div className="bg-white rounded-2xl shadow-sm p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FieldRow label="Ship To">
              <textarea value={form.ship_to_address} onChange={(e) => setField('ship_to_address', e.target.value)} rows={3} className={`${inp} py-2 resize-none`} />
            </FieldRow>
            <FieldRow label="Bill To">
              <textarea value={form.bill_to_address} onChange={(e) => setField('bill_to_address', e.target.value)} rows={3} className={`${inp} py-2 resize-none`} />
            </FieldRow>
            <FieldRow label="Shipping Type">
              <select value={form.shipping_type} onChange={(e) => setField('shipping_type', e.target.value)} className={sel}>
                {SHIPPING_TYPES.map((s) => (
                  <option key={s || '—'} value={s}>
                    {s || '—'}
                  </option>
                ))}
              </select>
            </FieldRow>
            <FieldRow label="Language">
              <input value={form.language} onChange={(e) => setField('language', e.target.value)} className={inp} />
            </FieldRow>
          </div>
        </div>
      )}

      {activeTab === 2 && (
        <div className="bg-white rounded-2xl shadow-sm p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FieldRow label="Journal Remark">
              <input value={form.journal_remark} onChange={(e) => setField('journal_remark', e.target.value)} className={inp} />
            </FieldRow>
            <FieldRow label="Payment Terms">
              <select value={form.payment_terms} onChange={(e) => setField('payment_terms', e.target.value)} className={sel}>
                {PAY_TERMS.map((p) => (
                  <option key={p || '—'} value={p}>
                    {p || '—'}
                  </option>
                ))}
              </select>
            </FieldRow>
            <FieldRow label="Business Partner Project">
              <input value={form.bp_project} onChange={(e) => setField('bp_project', e.target.value)} className={inp} placeholder="Project code…" />
            </FieldRow>
            <FieldRow label="Referenced PO No.">
              <input value={form.referenced_po} onChange={(e) => setField('referenced_po', e.target.value)} className={inp} placeholder="Base purchase order…" />
            </FieldRow>
          </div>
        </div>
      )}

      {activeTab === 3 && (
        <div className="bg-white rounded-2xl shadow-sm p-5">
          <p className="text-xs text-sap-textLight mb-3">Attachments (optional — browse / link files in a future release).</p>
          <table className="w-full text-xs border border-sap-border rounded-lg overflow-hidden">
            <thead className="bg-sap-blue/5">
              <tr>
                <th className="text-left px-3 py-2">#</th>
                <th className="text-left px-3 py-2">Target Path</th>
                <th className="text-left px-3 py-2">File Name</th>
                <th className="text-left px-3 py-2">Free Text</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td colSpan={4} className="px-3 py-6 text-center text-sap-textLight">
                  No attachments
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <div className="card p-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-3">
              <FieldRow label="Buyer">
                <input value={form.buyer} onChange={(e) => setField('buyer', e.target.value)} className={inp} />
              </FieldRow>
              <FieldRow label="Owner">
                <input value={form.owner} onChange={(e) => setField('owner', e.target.value)} className={inp} />
              </FieldRow>
            </div>
            <FieldRow label="Remarks">
              <textarea value={form.remarks} onChange={(e) => setField('remarks', e.target.value)} rows={4} className={`${inp} py-2 resize-y min-h-[5rem]`} />
            </FieldRow>
          </div>
          <div className="space-y-1 text-xs">
            <div className="flex justify-between text-sap-textLight">
              <span>Total Before Discount</span>
              <span className="font-medium bg-gray-100 px-2 py-0.5 rounded">{fmtCurrency(totalBeforeDiscount)}</span>
            </div>
            <div className="flex justify-between items-center text-sap-textLight gap-2">
              <span className="flex items-center gap-1">
                Discount %
                <input
                  type="number"
                  value={form.discount_pct}
                  onChange={(e) => setField('discount_pct', e.target.value)}
                  className="w-14 border border-sap-border rounded px-1 py-0.5 text-xs text-right"
                />
              </span>
              <span className="bg-gray-100 px-2 py-0.5 rounded">-{fmtCurrency(discountAmt)}</span>
            </div>
            <div className="flex justify-between items-center text-sap-textLight">
              <span className="flex items-center gap-1">
                Freight ↗
                <input
                  type="number"
                  value={form.freight_charges}
                  onChange={(e) => setField('freight_charges', e.target.value)}
                  className="w-20 border border-sap-border rounded px-1 py-0.5 text-xs text-right bg-white"
                />
              </span>
              <span className="bg-gray-100 px-2 py-0.5 rounded">{fmtCurrency(freight)}</span>
            </div>
            <div className="flex justify-between text-sap-textLight gap-2">
              <span className="flex items-center gap-2">
                <label className="flex items-center gap-1 cursor-pointer">
                  <input type="checkbox" checked={form.rounding} onChange={(e) => setField('rounding', e.target.checked)} className={chk} />
                  Rounding
                </label>
                <input readOnly value={`${fmtCurrency(roundingVal)} ${form.currency}`} className={`${ro} w-28 text-right text-2xs`} />
              </span>
            </div>
            <div className="flex justify-between text-sap-textLight">
              <span>Tax</span>
              <span className="flex items-center gap-1 bg-gray-100 px-2 py-0.5 rounded">
                <input
                  type="number"
                  value={form.tax_pct}
                  onChange={(e) => setField('tax_pct', e.target.value)}
                  className="w-12 border border-sap-border rounded px-1 text-2xs text-right bg-white"
                />
                %
                <span className="font-medium">{fmtCurrency(taxAmt)}</span>
              </span>
            </div>
            <div className="flex justify-between font-bold text-sap-text border-t border-sap-border/60 pt-2 text-sm mt-1">
              <span>Total Payment Due</span>
              <span>{fmtCurrency(grandTotal)} {form.currency}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative inline-flex">
          <button
            type="button"
            onClick={resetAll}
            className="px-3 py-1.5 text-xs font-semibold bg-slate-700 text-white rounded-l-lg hover:bg-slate-800"
          >
            Add & New
          </button>
          <button type="button" className="px-2 py-1.5 text-xs bg-slate-700 text-white border-l border-slate-600 rounded-r-lg hover:bg-slate-800" title="More">
            ▾
          </button>
        </div>
        <div className="relative inline-flex">
          <button
            type="button"
            onClick={resetDraft}
            className="px-3 py-1.5 text-xs font-semibold border border-slate-400 bg-slate-100 text-slate-800 rounded-l-lg hover:bg-slate-200"
          >
            Add Draft & New
          </button>
          <button type="button" className="px-2 py-1.5 text-xs border border-slate-400 border-l-0 bg-slate-100 rounded-r-lg hover:bg-slate-200" title="More">
            ▾
          </button>
        </div>
        <button
          type="button"
          onClick={() => navigate('/purchasing/orders')}
          className="px-3 py-1.5 text-xs border border-sap-border rounded-lg hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="button"
          className="px-3 py-1.5 text-xs font-semibold bg-slate-600 text-white rounded-lg hover:bg-slate-700"
          onClick={() => window.alert('Copy From: choose a Purchase Order / GRPO (integration pending).')}
        >
          Copy From
        </button>
        <button type="button" disabled className="px-3 py-1.5 text-xs border border-sap-border rounded-lg opacity-45 cursor-not-allowed">
          Copy To
        </button>
      </div>

      {showItemDD && itemSearchIdx !== null && itemDDRect != null &&
        createPortal(
          <div
            role="listbox"
            className="pointer-events-auto bg-white border border-sap-border rounded-lg shadow-lg overflow-y-auto"
            style={{
              position: 'fixed',
              top: itemDDRect.top,
              left: itemDDRect.left,
              width: itemDDRect.width,
              maxHeight: itemDDRect.maxHeight,
              zIndex: 100050,
            }}
          >
            {itemSearchLoading ? (
              <div className="px-3 py-2 text-2xs text-sap-textLight">Loading items…</div>
            ) : itemResults.length === 0 ? (
              <div className="px-3 py-2 text-2xs text-sap-textLight">No items</div>
            ) : (
              itemResults.map((it) => (
                <button
                  key={it.item_id}
                  type="button"
                  className="w-full text-left px-3 py-1.5 text-xs hover:bg-sap-blue/10 border-b border-sap-border/20 last:border-0"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    pickItem(it);
                  }}
                >
                  <span className="font-semibold text-sap-blue">{it.item_no}</span>
                  <span className="text-sap-textLight ml-2">{it.description}</span>
                </button>
              ))
            )}
          </div>,
          document.body,
        )}
    </div>
  );
}
